import React, { useCallback, useEffect, useState } from "react";
import AuthSignup from "./components/AuthSignup";
import LandingPage from "./components/LandingPage";
import TradingDashboard from "./components/TradingDashboard";
import ResultsDashboard from "./pages/ResultsDashboard";
import RadarDashboard from "./pages/RadarDashboard";
import AutoTraderDashboard from "./pages/AutoTraderDashboard";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import "./App.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SESSION_TOKEN_KEY = "spike_auth_token";
const FCM_TOKEN_KEY = "spike_fcm_token";
const PUSH_ONBOARDING_KEY = "spike_push_onboarding_pending";

function normalizePath(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

async function requestJson(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(options.headers || {}),
    },
  });
  const responseText = await response.text();
  let payload = {};

  if (responseText) {
    try {
      payload = JSON.parse(responseText);
    } catch (error) {
      payload = {
        message: responseText,
      };
    }
  }

  if (!response.ok) {
    throw new Error(payload.message || "Request failed");
  }

  return payload;
}

export default function App() {
  const [candles, setCandles] = useState([]);
  const [context, setContext] = useState({
    symbol: "Boom 1000",
    timeframe: 300,
  });
  const [chartApi, setChartApi] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [structure, setStructure] = useState(null);
  const [availableZones, setAvailableZones] = useState([]);
  const [availableOrderBlocks, setAvailableOrderBlocks] = useState([]);
  const [selectedZoneFilter, setSelectedZoneFilter] = useState("ALL");
  const [selectedOrderBlockFilter, setSelectedOrderBlockFilter] = useState("ALL");
  const [showAllZones, setShowAllZones] = useState(false);
  const [strategy, setStrategy] = useState("SMART_ZONE");
  const [user, setUser] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [bootingSession, setBootingSession] = useState(true);
  const [authMode, setAuthMode] = useState("signup");
  const [currentPath, setCurrentPath] = useState(() => normalizePath(window.location.pathname));
  const isDashboardRoute = currentPath === "/dashboard";
  const isResultsRoute = currentPath === "/dashboard/results";
  const isRadarRoute = currentPath === "/dashboard/radar";
  const isAutoTraderRoute = currentPath === "/dashboard/auto-trader";
  const isResetPasswordRoute = currentPath === "/reset-password";
  const isBlogIndexRoute = currentPath === "/blog";
  const isBlogPostRoute = currentPath.startsWith("/blog/") && currentPath !== "/blog/";

  const resetChartWorkspace = useCallback(() => {
    setCandles([]);
    setChartApi(null);
    setAnalysis(null);
    setStructure(null);
    setAvailableZones([]);
    setAvailableOrderBlocks([]);
    setSelectedZoneFilter("ALL");
    setSelectedOrderBlockFilter("ALL");
    setShowAllZones(false);
  }, []);

  const navigate = useCallback((pathname, replace = false) => {
    const isLeavingDashboard =
      currentPath.startsWith("/dashboard") && !pathname.startsWith("/dashboard");

    if (isLeavingDashboard) {
      resetChartWorkspace();
    }

    if (replace) {
      window.history.replaceState({}, "", pathname);
    } else if (window.location.pathname !== pathname) {
      window.history.pushState({}, "", pathname);
    }

    setCurrentPath(pathname);
  }, [currentPath, resetChartWorkspace]);

  const loadCurrentUser = useCallback(async (token) => {
    const payload = await requestJson("/api/user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { user: payload.user, subscriptionActive: payload.subscriptionActive };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizePath(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const existingToken = window.localStorage.getItem(SESSION_TOKEN_KEY);

    if (!existingToken) {
      setBootingSession(false);

      if (
        window.location.pathname === "/dashboard" ||
        window.location.pathname === "/dashboard/results" ||
        window.location.pathname === "/dashboard/radar" ||
        window.location.pathname === "/dashboard/auto-trader"
      ) {
        navigate("/auth", true);
      }

      return;
    }

    loadCurrentUser(existingToken)
      .then(({ user: nextUser, subscriptionActive: nextSubActive }) => {
        setUser(nextUser);
        setSubscriptionActive(nextSubActive);

        if (window.location.pathname === "/auth") {
          navigate("/dashboard", true);
        }
      })
      .catch(() => {
        window.localStorage.removeItem(SESSION_TOKEN_KEY);
        if (window.location.pathname !== "/reset-password") {
          navigate("/auth", true);
        }
      })
      .finally(() => {
        setBootingSession(false);
      });
  }, [loadCurrentUser, navigate]);

  const handleStartTrial = useCallback(() => {
    setAuthMode("signup");
    navigate("/auth");
  }, [navigate]);

  const handleLoginRoute = useCallback(() => {
    setAuthMode("login");
    navigate("/auth");
  }, [navigate]);

  const handleViewDashboard = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const handleNavigateBlog = useCallback(() => {
    navigate("/blog");
  }, [navigate]);

  const handleOpenRadarChart = useCallback(
    (symbol, timeframe) => {
      const market = symbol?.includes("Crash") ? "CRASH" : "BOOM";

      resetChartWorkspace();
      window.localStorage.setItem("spike-market", market);
      window.localStorage.setItem("spike-symbol", symbol);
      window.localStorage.setItem("spike-timeframe", String(timeframe));
      setContext({
        symbol,
        timeframe,
      });
      navigate("/dashboard");
    },
    [navigate, resetChartWorkspace]
  );

  async function handleSignup(form) {
    setAuthBusy(true);
    setAuthError("");

    try {
      const payload = await requestJson("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify(form),
      });

      window.localStorage.setItem(SESSION_TOKEN_KEY, payload.token);
      if (!window.localStorage.getItem(FCM_TOKEN_KEY)) {
        window.localStorage.setItem(PUSH_ONBOARDING_KEY, "1");
      }
      setUser(payload.user);
      setSubscriptionActive(payload.subscriptionActive);
      navigate("/dashboard");
    } catch (error) {
      setAuthError(error.message || "Failed to create account.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleLogin(form) {
    setAuthBusy(true);
    setAuthError("");

    try {
      const payload = await requestJson("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(form),
      });

      window.localStorage.setItem(SESSION_TOKEN_KEY, payload.token);
      if (!window.localStorage.getItem(FCM_TOKEN_KEY)) {
        window.localStorage.setItem(PUSH_ONBOARDING_KEY, "1");
      }
      setUser(payload.user);
      setSubscriptionActive(payload.subscriptionActive);
      navigate("/dashboard");
    } catch (error) {
      setAuthError(error.message || "Failed to log in.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleForgotPassword({ email }) {
    setAuthBusy(true);
    setAuthError("");

    try {
      await requestJson("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      setAuthError(error.message || "Failed to send reset email.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleResetPassword({ token, userId, password }) {
    setAuthBusy(true);
    setAuthError("");

    try {
      await requestJson("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, userId: Number(userId), password }),
      });
      setAuthMode("reset-success");
    } catch (error) {
      setAuthError(error.message || "Failed to reset password.");
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleLogout() {
    const token = window.localStorage.getItem(SESSION_TOKEN_KEY);
    const fcmToken = window.localStorage.getItem(FCM_TOKEN_KEY);

    if (token) {
      try {
        if (fcmToken) {
          await requestJson("/api/alerts/fcm/unregister", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              token: fcmToken,
            }),
          });
        }

        await requestJson("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {}
    }

    window.localStorage.removeItem(SESSION_TOKEN_KEY);
    window.localStorage.removeItem(FCM_TOKEN_KEY);
    setUser(null);
    setSubscriptionActive(null);
    resetChartWorkspace();
    navigate("/");
  }

  if (bootingSession) {
    return (
      <div className="auth-shell">
        <div className="auth-card auth-card-compact">
          <div className="auth-kicker">Session</div>
          <h1 className="auth-title">Restoring workspace</h1>
          <p className="auth-copy">Checking your saved session...</p>
        </div>
      </div>
    );
  }

  if (isBlogIndexRoute) {
    return <Blog onNavigate={navigate} />;
  }

  if (isBlogPostRoute) {
    const slug = decodeURIComponent(currentPath.slice("/blog/".length));
    return <BlogPost slug={slug} onNavigate={navigate} />;
  }

  if (!user) {
    if (isResetPasswordRoute) {
      const urlParams = new URLSearchParams(window.location.search);
      const resetToken = urlParams.get("token") || "";
      const resetUserId = urlParams.get("uid") || "";
      return (
        <AuthSignup
          mode="reset"
          onModeChange={(nextMode) => {
            setAuthMode(nextMode);
            if (nextMode === "login") navigate("/auth");
          }}
          onForgotPassword={handleForgotPassword}
          onResetPassword={handleResetPassword}
          resetToken={resetToken}
          resetUserId={resetUserId}
          busy={authBusy}
          error={authError}
        />
      );
    }

    if (currentPath === "/auth") {
      return (
        <AuthSignup
          mode={authMode}
          onModeChange={(nextMode) => {
            setAuthMode(nextMode);
            setAuthError("");
          }}
          onSignup={handleSignup}
          onLogin={handleLogin}
          onForgotPassword={handleForgotPassword}
          busy={authBusy}
          error={authError}
        />
      );
    }

    return (
      <LandingPage
        onStartTrial={handleStartTrial}
        onViewDashboard={handleViewDashboard}
        onLogin={handleLoginRoute}
        onNavigateBlog={handleNavigateBlog}
        isAuthenticated={false}
        analysis={analysis}
        context={context}
      />
    );
  }

  if (subscriptionActive === false) {
    return (
      <div className="auth-shell">
        <div className="auth-card auth-card-compact">
          <div className="auth-kicker">Subscription</div>
          <h1 className="auth-title">Your subscription has expired</h1>
          <p className="auth-copy">
            Your plan is no longer active. Please contact the administrator to renew your
            subscription.
          </p>
          <button className="auth-token-button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </div>
    );
  }

  if (isDashboardRoute) {
    return (
      <TradingDashboard
        user={user}
        onLogout={handleLogout}
        onNavigateResults={() => navigate("/dashboard/results")}
        onNavigateRadar={() => navigate("/dashboard/radar")}
        onNavigateAutoTrader={() => navigate("/dashboard/auto-trader")}
        candles={candles}
        setCandles={setCandles}
        context={context}
        setContext={setContext}
        chartApi={chartApi}
        setChartApi={setChartApi}
        analysis={analysis}
        setAnalysis={setAnalysis}
        structure={structure}
        setStructure={setStructure}
        availableZones={availableZones}
        setAvailableZones={setAvailableZones}
        availableOrderBlocks={availableOrderBlocks}
        setAvailableOrderBlocks={setAvailableOrderBlocks}
        selectedZoneFilter={selectedZoneFilter}
        setSelectedZoneFilter={setSelectedZoneFilter}
        selectedOrderBlockFilter={selectedOrderBlockFilter}
        setSelectedOrderBlockFilter={setSelectedOrderBlockFilter}
        showAllZones={showAllZones}
        setShowAllZones={setShowAllZones}
        strategy={strategy}
        setStrategy={setStrategy}
      />
    );
  }

  if (isResultsRoute) {
    return (
      <ResultsDashboard
        user={user}
        onLogout={handleLogout}
        onNavigateLive={() => navigate("/dashboard")}
        onNavigateResults={() => navigate("/dashboard/results")}
        onNavigateRadar={() => navigate("/dashboard/radar")}
        onNavigateAutoTrader={() => navigate("/dashboard/auto-trader")}
      />
    );
  }

  if (isRadarRoute) {
    return (
      <RadarDashboard
        user={user}
        onLogout={handleLogout}
        onNavigateLive={() => navigate("/dashboard")}
        onNavigateResults={() => navigate("/dashboard/results")}
        onNavigateRadar={() => navigate("/dashboard/radar")}
        onNavigateAutoTrader={() => navigate("/dashboard/auto-trader")}
        onOpenChart={handleOpenRadarChart}
      />
    );
  }

  if (isAutoTraderRoute) {
    return (
      <AutoTraderDashboard
        user={user}
        onLogout={handleLogout}
        onNavigateLive={() => navigate("/dashboard")}
        onNavigateResults={() => navigate("/dashboard/results")}
        onNavigateRadar={() => navigate("/dashboard/radar")}
        onNavigateAutoTrader={() => navigate("/dashboard/auto-trader")}
      />
    );
  }

  return (
    <LandingPage
      onStartTrial={handleViewDashboard}
      onViewDashboard={handleViewDashboard}
      onLogin={handleViewDashboard}
      onNavigateBlog={handleNavigateBlog}
      isAuthenticated
      analysis={analysis}
      context={context}
    />
  );
}
